import React, { useEffect, useRef } from "react";

/**
 * Canvasbaserat partikelfält som körs i requestAnimationFrame.
 * Ett fält per tema: snö, blomblad, konfetti, fjädrar, höstlöv eller gnistor.
 * Respekterar prefers-reduced-motion (ritar då en stillbild) och
 * pausar automatiskt när fliken inte är synlig.
 */
export type ParticleKind = "snow" | "petals" | "confetti" | "feathers" | "leaves" | "embers";

interface ParticleFieldProps {
  kind: ParticleKind;
  dark: boolean;
  /** Skalar antalet partiklar (1 = standard). */
  density?: number;
}

interface Particle {
  x: number;
  y: number;
  r: number;
  vx: number;
  vy: number;
  rot: number;
  vrot: number;
  phase: number;
  alpha: number;
  color: string;
  depth: number; // 0..1, används för storlek/hastighet
}

const PETAL_COLORS = ["#ffffff", "#fbcfe8", "#fde68a", "#f9a8d4", "#fef3c7"];
const FEATHER_COLORS = ["#fde68a", "#fcd34d", "#fbbf24", "#ffffff", "#fef3c7", "#f59e0b"];
const CONFETTI_COLORS = ["#ffffff", "#7dd3fc", "#0ea5e9", "#ffffff", "#bae6fd"];
const LEAF_COLORS_LIGHT = ["#ea580c", "#c2410c", "#d97706", "#b45309", "#dc2626"];
const LEAF_COLORS_DARK = ["#fb923c", "#f97316", "#fbbf24", "#fdba74", "#f87171"];

const rand = (min: number, max: number) => min + Math.random() * (max - min);
const pick = <T,>(arr: readonly T[]) => arr[Math.floor(Math.random() * arr.length)];

const baseCount = (kind: ParticleKind, w: number, h: number) => {
  const area = (w * h) / (1280 * 720);
  switch (kind) {
    case "snow":
      return Math.round(140 * area);
    case "petals":
      return Math.round(45 * area);
    case "confetti":
      return Math.round(40 * area);
    case "feathers":
      return Math.round(28 * area);
    case "leaves":
      return Math.round(34 * area);
    case "embers":
      return Math.round(60 * area);
  }
};

const spawn = (kind: ParticleKind, w: number, h: number, dark: boolean, initial: boolean): Particle => {
  const depth = Math.random();
  const p: Particle = {
    x: rand(0, w),
    y: 0,
    r: 2,
    vx: 0,
    vy: 0,
    rot: rand(0, Math.PI * 2),
    vrot: rand(-0.02, 0.02),
    phase: rand(0, Math.PI * 2),
    alpha: 1,
    color: "#ffffff",
    depth,
  };
  switch (kind) {
    case "snow":
      p.r = 1 + depth * 3.2;
      p.vy = 0.35 + depth * 0.9;
      p.vx = rand(-0.15, 0.15);
      p.alpha = 0.35 + depth * 0.6;
      p.color = "#ffffff";
      p.y = initial ? rand(-h, h) : rand(-40, -5);
      break;
    case "petals":
      p.r = 3 + depth * 4;
      p.vy = 0.45 + depth * 0.7;
      p.vx = rand(-0.2, 0.35);
      p.vrot = rand(-0.04, 0.04);
      p.alpha = 0.6 + depth * 0.4;
      p.color = pick(PETAL_COLORS);
      p.y = initial ? rand(-h, h) : rand(-40, -5);
      break;
    case "feathers":
      p.r = 5 + depth * 7;
      p.vy = 0.22 + depth * 0.35;
      p.vx = rand(-0.1, 0.1);
      p.vrot = rand(-0.015, 0.015);
      p.alpha = 0.55 + depth * 0.4;
      p.color = pick(FEATHER_COLORS);
      p.y = initial ? rand(-h, h) : rand(-60, -10);
      break;
    case "leaves":
      p.r = 4 + depth * 5;
      p.vy = 0.5 + depth * 0.8;
      p.vx = rand(0.1, 0.6);
      p.vrot = rand(-0.05, 0.05);
      p.alpha = 0.6 + depth * 0.4;
      p.color = pick(dark ? LEAF_COLORS_DARK : LEAF_COLORS_LIGHT);
      p.y = initial ? rand(-h, h) : rand(-40, -5);
      p.x = initial ? rand(0, w) : rand(-w * 0.2, w);
      break;
    case "confetti":
      // Blåvit festkonfetti (Oktoberfest) som tumlar ner från himlen.
      p.r = 3 + depth * 4;
      p.vy = 0.4 + depth * 0.7;
      p.vx = rand(-0.2, 0.2);
      p.vrot = rand(-0.06, 0.06);
      p.alpha = 0.7 + depth * 0.3;
      p.color = pick(CONFETTI_COLORS);
      p.y = initial ? rand(-h, h) : rand(-40, -5);
      break;
    case "embers":
      p.r = 0.8 + depth * 1.8;
      p.vy = -(0.15 + depth * 0.35);
      p.vx = rand(-0.1, 0.1);
      p.alpha = 0.3 + depth * 0.7;
      p.color = dark ? "#fbbf24" : "#f97316";
      p.y = initial ? rand(0, h) : h + rand(5, 40);
      break;
  }
  return p;
};

const draw = (ctx: CanvasRenderingContext2D, kind: ParticleKind, p: Particle, t: number) => {
  ctx.save();
  ctx.globalAlpha = p.alpha;
  ctx.translate(p.x, p.y);
  switch (kind) {
    case "snow": {
      ctx.fillStyle = p.color;
      ctx.shadowColor = "rgba(255,255,255,0.8)";
      ctx.shadowBlur = p.depth > 0.6 ? 6 : 0;
      ctx.beginPath();
      ctx.arc(0, 0, p.r, 0, Math.PI * 2);
      ctx.fill();
      break;
    }
    case "petals": {
      ctx.rotate(p.rot);
      ctx.fillStyle = p.color;
      ctx.beginPath();
      ctx.ellipse(0, 0, p.r, p.r * 0.55, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.globalAlpha = p.alpha * 0.5;
      ctx.fillStyle = "#ffffff";
      ctx.beginPath();
      ctx.ellipse(-p.r * 0.3, -p.r * 0.15, p.r * 0.35, p.r * 0.18, 0, 0, Math.PI * 2);
      ctx.fill();
      break;
    }
    case "feathers": {
      ctx.rotate(p.rot + Math.sin(t * 0.0012 + p.phase) * 0.35);
      ctx.fillStyle = p.color;
      ctx.beginPath();
      ctx.moveTo(0, -p.r);
      ctx.quadraticCurveTo(p.r * 0.55, -p.r * 0.2, 0, p.r);
      ctx.quadraticCurveTo(-p.r * 0.55, -p.r * 0.2, 0, -p.r);
      ctx.fill();
      ctx.strokeStyle = "rgba(255,255,255,0.55)";
      ctx.lineWidth = 0.8;
      ctx.beginPath();
      ctx.moveTo(0, -p.r * 0.85);
      ctx.lineTo(0, p.r * 0.85);
      ctx.stroke();
      break;
    }
    case "leaves": {
      ctx.rotate(p.rot);
      ctx.fillStyle = p.color;
      ctx.beginPath();
      ctx.moveTo(0, -p.r);
      ctx.bezierCurveTo(p.r * 0.9, -p.r * 0.6, p.r * 0.9, p.r * 0.6, 0, p.r);
      ctx.bezierCurveTo(-p.r * 0.9, p.r * 0.6, -p.r * 0.9, -p.r * 0.6, 0, -p.r);
      ctx.fill();
      ctx.strokeStyle = "rgba(0,0,0,0.25)";
      ctx.lineWidth = 0.7;
      ctx.beginPath();
      ctx.moveTo(0, -p.r * 0.8);
      ctx.lineTo(0, p.r * 0.9);
      ctx.stroke();
      break;
    }
    case "confetti": {
      ctx.rotate(p.rot);
      // Perspektiv: bredden pulserar så biten ser ut att tumla
      const squeeze = 0.35 + 0.65 * Math.abs(Math.sin(t * 0.003 + p.phase));
      ctx.fillStyle = p.color;
      ctx.fillRect(-p.r * squeeze, -p.r * 0.6, p.r * 2 * squeeze, p.r * 1.2);
      ctx.globalAlpha = p.alpha * 0.35;
      ctx.fillStyle = "#000000";
      ctx.fillRect(-p.r * squeeze, p.r * 0.2, p.r * 2 * squeeze, p.r * 0.4);
      break;
    }
    case "embers": {
      const flicker = 0.7 + 0.3 * Math.sin(t * 0.004 + p.phase * 3);
      ctx.globalAlpha = p.alpha * flicker;
      ctx.fillStyle = p.color;
      ctx.shadowColor = p.color;
      ctx.shadowBlur = 8;
      ctx.beginPath();
      ctx.arc(0, 0, p.r, 0, Math.PI * 2);
      ctx.fill();
      break;
    }
  }
  ctx.restore();
};

const ParticleField: React.FC<ParticleFieldProps> = ({ kind, dark, density = 1 }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const reduceMotion =
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    let w = 0;
    let h = 0;
    let dpr = 1;
    let particles: Particle[] = [];
    let raf = 0;
    let last = performance.now();

    const resize = () => {
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      w = window.innerWidth;
      h = window.innerHeight;
      canvas.width = Math.floor(w * dpr);
      canvas.height = Math.floor(h * dpr);
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      const target = Math.max(8, Math.round(baseCount(kind, w, h) * density));
      if (particles.length > target) {
        particles.length = target;
      } else {
        while (particles.length < target) {
          particles.push(spawn(kind, w, h, dark, true));
        }
      }
    };

    const step = (now: number) => {
      const dt = Math.min(50, now - last) / 16.67; // normaliserat till ~60 fps
      last = now;
      ctx.clearRect(0, 0, w, h);
      for (let i = 0; i < particles.length; i++) {
        const p = particles[i];
        const sway = Math.sin(now * 0.001 + p.phase);
        switch (kind) {
          case "snow":
            p.x += (p.vx + sway * 0.35 * (0.4 + p.depth)) * dt;
            p.y += p.vy * dt;
            break;
          case "petals":
          case "feathers":
            p.x += (p.vx + sway * 0.6) * dt;
            p.y += (p.vy + Math.cos(now * 0.0015 + p.phase) * 0.15) * dt;
            p.rot += p.vrot * dt;
            break;
          case "leaves":
            p.x += (p.vx + sway * 0.5) * dt;
            p.y += (p.vy + Math.cos(now * 0.002 + p.phase) * 0.25) * dt;
            p.rot += p.vrot * dt;
            break;
          case "confetti":
            p.x += (p.vx + sway * 0.8) * dt;
            p.y += (p.vy + Math.cos(now * 0.002 + p.phase) * 0.2) * dt;
            p.rot += p.vrot * dt;
            break;
          case "embers":
            p.x += (p.vx + sway * 0.25) * dt;
            p.y += p.vy * dt;
            break;
        }
        const out =
          p.y > h + 40 || p.y < -80 || p.x < -w * 0.25 || p.x > w * 1.25;
        if (out) {
          particles[i] = spawn(kind, w, h, dark, false);
          continue;
        }
        draw(ctx, kind, p, now);
      }
      if (!reduceMotion) {
        raf = window.requestAnimationFrame(step);
      }
    };

    resize();
    window.addEventListener("resize", resize);
    raf = window.requestAnimationFrame(step);

    return () => {
      window.cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
    };
  }, [kind, dark, density]);

  return <canvas ref={canvasRef} className="hol-particles" aria-hidden="true" />;
};

export default ParticleField;
